/**
 * postMessage protocol between the admin host and sandboxed plugin frames
 * (extensions contract v1). The plugin document has an opaque origin and no
 * network access; every request is mediated and authorized by the host.
 */
export const PROTOCOL_VERSION = 1
export const PLUGIN_SOURCE = 'gx-cms-plugin'
export const HOST_SOURCE = 'gx-cms-host'

/** Capability required for each API route a plugin UI may call through the bridge. */
export const BRIDGE_ROUTES: Array<{
  method: string
  pattern: RegExp
  capability: string
}> = [
  {
    method: 'GET',
    pattern: /^\/content-types(\/[^/]+)?$/,
    capability: 'content.type.read',
  },
  {
    method: 'GET',
    pattern: /^\/entries(\/stats)?$/,
    capability: 'content.entry.read',
  },
  {
    method: 'GET',
    pattern: /^\/entries\/[0-9a-f-]{36}(\/versions(\/\d+)?)?$/,
    capability: 'content.entry.read',
  },
  {
    method: 'GET',
    pattern: /^\/delivery\/[a-z0-9_]+(\/[^/]+)?$/,
    capability: 'content.entry.read',
  },
  {
    method: 'POST',
    pattern: /^\/entries$/,
    capability: 'content.entry.create',
  },
  {
    method: 'PUT',
    pattern: /^\/entries\/[0-9a-f-]{36}$/,
    capability: 'content.entry.update',
  },
  {
    method: 'POST',
    pattern: /^\/entries\/[0-9a-f-]{36}\/(submit|archive|restore)$/,
    capability: 'content.entry.update',
  },
  {
    method: 'POST',
    pattern: /^\/entries\/[0-9a-f-]{36}\/(publish|unpublish)$/,
    capability: 'content.entry.publish',
  },
  {
    method: 'GET',
    pattern: /^\/media\/assets(\/[0-9a-f-]{36})?$/,
    capability: 'media.asset.read',
  },
  {
    method: 'GET',
    pattern: /^\/media\/assets\/[0-9a-f-]{36}\/signed-url$/,
    capability: 'media.asset.read',
  },
  {
    method: 'PATCH',
    pattern: /^\/media\/assets\/[0-9a-f-]{36}$/,
    capability: 'media.asset.upload',
  },
  {
    method: 'GET',
    pattern: /^\/plugins\/self\/storage\/[A-Za-z0-9_.-]+(\/[A-Za-z0-9_.:-]+)?$/,
    capability: 'storage.plugin',
  },
]

/** Returns the capability a bridged API call needs, or null if the route is not allowed at all. */
export function requiredCapability(
  method: string,
  path: string,
): string | null {
  const clean = path.split('?')[0]

  if (clean.includes('..') || !clean.startsWith('/')) return null

  return (
    BRIDGE_ROUTES.find((r) => r.method === method && r.pattern.test(clean))
      ?.capability ?? null
  )
}

/**
 * Applies a visual theme to the document (ADR-056, ADR-068): `data-design`
 * names it, and every theme but GXLabs also gets the shared soft layout.
 */
export function applyDesign(design: string, root = document.documentElement) {
  root.dataset.design = design
  if (design === 'gxlabs') delete root.dataset.shape
  else root.dataset.shape = 'soft'
}
